import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { BadgePercentIcon, XmarkIcon } from '@revfluence/fresh-icons/regular/esm';
import { CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@frontend/shadcn/components/ui/sheet';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { Label } from '@frontend/shadcn/components/ui/label';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@frontend/shadcn/components/ui/collapsible';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@frontend/shadcn/components/ui/dialog';

import { ChevronDownIcon, ChevronUpIcon, CalendarDaysIcon } from '@revfluence/fresh-icons/solid/esm';

import {
  AutomatedPayoutType,
  OfferAutomationConfigInput,
  TierAggregationDurationTypes,
} from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { Tag } from '@frontend/app/refresh-components';
import { Alert, AlertDescription } from '@frontend/shadcn/components/ui/alert';
import { IAffiliateLinksFormValues, IShopifyPromoCodeFormValues, IPayoutVariant, FormAsyncAction, OfferFormAsyncActions } from '../../../../types';
import { IRefreshPayoutProps } from '../../../../utils/OfferFormUtils';
import { AutomatedOfferStartDate } from './AutomatedOfferStartDate';

interface AutomatePayoutsDrawerProps extends Partial<IRefreshPayoutProps> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  offerDetails: IAffiliateLinksFormValues | IShopifyPromoCodeFormValues;
  handleFormAsyncActions?: (value: FormAsyncAction) => void;
}

interface AutomatedTier {
  id: string;
  threshold?: number;
  payoutLabel?: string;
}

const generateUniqueTierId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Helper function to derive processed offer configuration
const getProcessedOfferConfig = (
  detailsConfig?: Partial<OfferAutomationConfigInput>,
): Partial<OfferAutomationConfigInput> => {
  const config = detailsConfig || {};
  return {
    ...config,
    automationType: config?.automationType || AutomatedPayoutType.SALE,
    tierAggregationDuration: config?.tierAggregationDuration || TierAggregationDurationTypes.TOTAL,
    automationStartDate: config.automationStartDate,
    showInCreatorPortal: typeof config.showInCreatorPortal === 'boolean' ? config.showInCreatorPortal : true,
    isActive: config.isActive,
  } as OfferAutomationConfigInput; // Ensure it aligns with the expected full type for clarity
};

export const AutomatePayoutsDrawer: React.FC<AutomatePayoutsDrawerProps> = ({
  open,
  onOpenChange,
  onCancel,
  offerDetails,
  handleFormAsyncActions
}) => {
  const processPayoutOptionsToAutomatedTiers = (
    options: IPayoutVariant[]
  ): AutomatedTier[] => {
    const tiers = (options || [])
      .filter((o) => o.automationThreshold !== undefined && o.automationThreshold !== null)
      .map((o) => ({
        id: generateUniqueTierId(),
        threshold: o.automationThreshold,
        payoutLabel: o.label,
      }));

    if (tiers.length > 0) {
      tiers[0].threshold = 0;
    }
    return tiers;
  };

  // Current working states
  const [payoutOptions, setPayoutOptions] = useState<IPayoutVariant[]>(offerDetails.payoutOptions || []);
  const [offerAutomationConfig, setOfferAutomationConfig] = useState<Partial<OfferAutomationConfigInput>>(() =>
    getProcessedOfferConfig(offerDetails.offerAutomationConfig)
  );
  const [automatedTiers, setAutomatedTiers] = useState<AutomatedTier[]>(() => {
    return processPayoutOptionsToAutomatedTiers(offerDetails.payoutOptions || []);
  });

  const [isSettingsExpanded, setIsSettingsExpanded] = useState(offerAutomationConfig?.tierAggregationDuration === TierAggregationDurationTypes.TOTAL);
  const [showConfirmActivationDialog, setShowConfirmActivationDialog] = useState(false);
  const [showPausePromptDialog, setShowPausePromptDialog] = useState(false);
  const [showSelectDefaultPayoutDialog, setShowSelectDefaultPayoutDialog] = useState(false);
  const [selectedDefaultPayoutLabel, setSelectedDefaultPayoutLabel] = useState<string | undefined>(undefined);
  const [tierThresholdErrors, setTierThresholdErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    // Calculate the state based on the current offerDetails prop
    const newPayoutOptions = offerDetails.payoutOptions || [];
    const processedNewOfferConfig = getProcessedOfferConfig(offerDetails.offerAutomationConfig);
    const newAutomatedTiers = processPayoutOptionsToAutomatedTiers(newPayoutOptions);

    // Update the working states
    setPayoutOptions(newPayoutOptions);
    setOfferAutomationConfig(processedNewOfferConfig);
    setAutomatedTiers(newAutomatedTiers);
  }, [open, offerDetails]);

  const compareOfferAutomationConfig = (configA: Partial<OfferAutomationConfigInput>, configB: Partial<OfferAutomationConfigInput>): boolean => {
    if (!configA || !configB) return configA === configB;
    return (
      configA.automationType === configB.automationType &&
      configA.tierAggregationDuration === configB.tierAggregationDuration &&
      configA.automationStartDate === configB.automationStartDate &&
      configA.showInCreatorPortal === configB.showInCreatorPortal &&
      configA.isActive === configB.isActive
    );
  };

  const compareAutomatedTiers = (tiersA: AutomatedTier[], tiersB: AutomatedTier[]): boolean => {
    if (tiersA.length !== tiersB.length) {
      return false;
    }
    return !tiersA.some((tier, i) => tier.threshold !== tiersB[i].threshold || tier.payoutLabel !== tiersB[i].payoutLabel);
  };

  const isRequiredFieldsMissing = useMemo(() => {
    const missingTiers = automatedTiers.some((tier) => {
      return !tier.id || tier.threshold === undefined || !tier.payoutLabel;
    });
    const hasThresholdErrors = Object.values(tierThresholdErrors).some((error) => error !== undefined);

    const minimumOneTier = automatedTiers.length > 0;

    const requiredAutomationStartDate = offerAutomationConfig?.tierAggregationDuration === TierAggregationDurationTypes.TOTAL
      && !offerAutomationConfig?.automationStartDate;

    return missingTiers || requiredAutomationStartDate || !minimumOneTier || hasThresholdErrors;
  }, [automatedTiers, offerAutomationConfig, tierThresholdErrors]);

  const hasUnsavedChanges = useMemo(() => {
    if (!open) {
      return false;
    }
    // if its a new offerAutomationConfig, we don't have a baseline to compare to
    // For a new config, any change from default (empty tiers, inactive) is an unsaved change.
    if (!offerAutomationConfig?.id) {
      // Check if it's different from a freshly processed empty config
      const freshEmptyConfig = getProcessedOfferConfig({});
      const configIsDefault = compareOfferAutomationConfig(offerAutomationConfig, freshEmptyConfig);
      const tiersAreEmpty = automatedTiers.length === 0;
      if (configIsDefault && tiersAreEmpty) {
        return false; // New and untouched
      }
      return true; // New and modified
    }

    // Derive baseline directly from current props for comparison
    const baselineProcessedOfferConfig = getProcessedOfferConfig(offerDetails.offerAutomationConfig);
    const baselineAutomatedTiers = processPayoutOptionsToAutomatedTiers(offerDetails.payoutOptions || []);

    const configChanged = !compareOfferAutomationConfig(offerAutomationConfig, baselineProcessedOfferConfig);
    const tiersChanged = !compareAutomatedTiers(automatedTiers, baselineAutomatedTiers);
    return configChanged || tiersChanged;
  }, [
    open,
    offerAutomationConfig,
    automatedTiers,
    offerDetails,
  ]);

  const saveAutomationSettings = (newIsActive?: boolean) => {
    const finalPayoutOptions = payoutOptions.map((opt) => {
      const tier = automatedTiers.find((t) => t.payoutLabel === opt.label);
      // If activating or settings change while active, thresholds come from tiers.
      // If newIsActive is explicitly false (deactivating path, handled by executeDeactivationConfirmed), this won't be the primary path for thresholds.
      // If just saving settings and isActive is already false, tiers might be empty/ignored.
      const isActiveEffective = newIsActive !== undefined ? newIsActive : offerAutomationConfig.isActive;
      return {
        ...opt,
        automationThreshold: isActiveEffective && tier ? tier.threshold : undefined,
      };
    });

    const updatedConfig = {
      ...offerAutomationConfig,
      isActive: newIsActive !== undefined ? newIsActive : !!offerAutomationConfig.isActive,
      automationType: offerAutomationConfig?.automationType || AutomatedPayoutType.SALE,
      tierAggregationDuration: offerAutomationConfig?.tierAggregationDuration || TierAggregationDurationTypes.TOTAL,
    } as OfferAutomationConfigInput;

    if (updatedConfig.tierAggregationDuration !== TierAggregationDurationTypes.TOTAL) {
      updatedConfig.automationStartDate = undefined;
    }

    handleFormAsyncActions?.({
      action: OfferFormAsyncActions.UPDATE_FIELD,
      payload: { key: 'payoutOptions', value: finalPayoutOptions },
    });

    handleFormAsyncActions?.({
      action: OfferFormAsyncActions.UPDATE_FIELD,
      payload: { key: 'offerAutomationConfig', value: updatedConfig },
    });

    setPayoutOptions(finalPayoutOptions);
    setOfferAutomationConfig(updatedConfig);
    onOpenChange(false);
  };

  const handleDone = () => {
    if (offerAutomationConfig?.id && !hasUnsavedChanges) {
      onOpenChange(false);
      return;
    }
    // if new and untouched, also just close
    if (!offerAutomationConfig?.id && !hasUnsavedChanges) {
      onOpenChange(false);
      return;
    }

    const baselineProcessedOfferConfig = getProcessedOfferConfig(offerDetails.offerAutomationConfig);
    const initialIsActive = !!baselineProcessedOfferConfig.isActive;
    const currentIsActive = !!offerAutomationConfig.isActive;

    if (!initialIsActive && currentIsActive) { // Activating
      setShowConfirmActivationDialog(true);
    } else if (initialIsActive && !currentIsActive) { // Deactivating
      setShowPausePromptDialog(true);
    } else {
      saveAutomationSettings();
    }
  };

  const executeActivationConfirmed = () => {
    saveAutomationSettings(true);
    setShowConfirmActivationDialog(false);
  };

  const cancelActivationConfirmation = () => {
    setShowConfirmActivationDialog(false);
    setOfferAutomationConfig((prevConfig) => ({
      ...prevConfig,
      isActive: false,
    }));
  };

  const handleProceedToSelectDefault = () => {
    const existingStaticDefault = payoutOptions.find((p) => p.isDefault && (p.automationThreshold === undefined || p.automationThreshold === null));
    const anyStatic = payoutOptions.find((p) => p.automationThreshold === undefined || p.automationThreshold === null);
    const firstOverall = payoutOptions[0];
    setSelectedDefaultPayoutLabel(existingStaticDefault?.label || anyStatic?.label || firstOverall?.label);

    setShowPausePromptDialog(false);
    if (payoutOptions.length === 0) { // Edge case, no payouts to select from, proceed with deactivation
      executeDeactivationConfirmed();
    } else {
      setShowSelectDefaultPayoutDialog(true);
    }
  };

  const executeDeactivationConfirmed = () => {
    let finalPayoutOptions = payoutOptions;

    if (selectedDefaultPayoutLabel && payoutOptions.length > 0) {
      finalPayoutOptions = payoutOptions.map((opt) => ({
        ...opt,
        isDefault: opt.label === selectedDefaultPayoutLabel,
      }));
    } else {
      // If no label selected (e.g. payoutOptions was empty), just clear thresholds
      finalPayoutOptions = payoutOptions.map((opt) => ({
        ...opt,
      }));
    }

    const updatedConfig = {
      ...offerAutomationConfig,
      isActive: false,
    } as OfferAutomationConfigInput;

    handleFormAsyncActions?.({
      action: OfferFormAsyncActions.UPDATE_FIELD,
      payload: { key: 'payoutOptions', value: finalPayoutOptions },
    });

    handleFormAsyncActions?.({
      action: OfferFormAsyncActions.UPDATE_FIELD,
      payload: { key: 'offerAutomationConfig', value: updatedConfig },
    });

    setPayoutOptions(finalPayoutOptions);
    setOfferAutomationConfig(updatedConfig);
    setShowSelectDefaultPayoutDialog(false);
    onOpenChange(false);
  };

  const cancelPauseProcess = () => {
    setShowPausePromptDialog(false);
    setShowSelectDefaultPayoutDialog(false);
    setOfferAutomationConfig((prevConfig) => ({
      ...prevConfig,
      isActive: true,
    }));
  };

  const handleCancel = () => {
    const initialConfigFromDetails = offerDetails.offerAutomationConfig || {};
    const processedConfigFromDetails = getProcessedOfferConfig(initialConfigFromDetails);

    setOfferAutomationConfig(processedConfigFromDetails);
    setAutomatedTiers(
      processPayoutOptionsToAutomatedTiers(offerDetails.payoutOptions || [])
    );
    onOpenChange(false);
    onCancel();
  };

  const handleAutomationTypeChange = (value: string) => {
    setOfferAutomationConfig((prevConfig) => ({
      ...prevConfig,
      automationType: value as AutomatedPayoutType,
    }));
  };

  const handleAutomationStartDateChange = (date: Date | null) => {
    setOfferAutomationConfig((prevConfig) => ({
      ...prevConfig,
      automationStartDate: date ? date.toISOString() : null,
    }));
  };

  const handleTierAggregationDurationChange = (value: string) => {
    if (value === TierAggregationDurationTypes.TOTAL) {
      setIsSettingsExpanded(true);
    }
    setOfferAutomationConfig((prevConfig) => ({
      ...prevConfig,
      tierAggregationDuration: value as TierAggregationDurationTypes,
    }));
  };

  const handleAddTier = () => {
    setAutomatedTiers((prevTiers) => [
      ...prevTiers,
      {
        id: generateUniqueTierId(),
        threshold: prevTiers.length === 0 ? 0 : undefined,
        payoutLabel: undefined
      },
    ]);
  };

  const handleDeleteTier = (tierId: string) => {
    const tierToDelete = automatedTiers.find((tier) => tier.id === tierId);
    if (!tierToDelete) return;

    setAutomatedTiers((prevTiers) => prevTiers.filter((tier) => tier.id !== tierId));

    if (tierToDelete.payoutLabel) {
      const updatedPayoutOptions = payoutOptions.map((option) => {
        if (option.label === tierToDelete.payoutLabel) {
          return { ...option, automationThreshold: undefined };
        }
        return option;
      });
      // remove error for this tier if it exists
      setTierThresholdErrors((prevErrors) => {
        const { [tierId]: _, ...rest } = prevErrors;
        return rest;
      });
      setPayoutOptions(updatedPayoutOptions);
    }
  };

  const handleTierChange = (tierId: string, field: keyof AutomatedTier, value: string | number) => {
    setAutomatedTiers((prevTiers) =>
      prevTiers.map((tier) => {
        if (tier.id === tierId) {
          const updatedTier: AutomatedTier = { ...tier };

          if (field === 'payoutLabel') {
            const selectedOption = payoutOptions.find((opt) => opt.label === value);
            updatedTier.payoutLabel = selectedOption?.label;
          } else if (field === 'threshold') {
            const numValue = value === '' ? undefined : parseFloat(value as string);
            updatedTier.threshold = isNaN(numValue) ? undefined : numValue;

            // Clear error for this tier when value changes, validation will happen on blur
            setTierThresholdErrors((prevErrors) => ({
              ...prevErrors,
              [tierId]: undefined,
            }));
          }

          return updatedTier;
        }
        return tier;
      }),
    );
  };

  const handleThresholdBlur = (tierId: string, tierIndex: number) => {
    const currentTier = automatedTiers.find((t) => t.id === tierId);
    if (!currentTier || currentTier.threshold === undefined || tierIndex === 0) {
      setTierThresholdErrors((prevErrors) => ({
        ...prevErrors,
        [tierId]: undefined, // Clear error if first tier or no threshold
      }));
      return;
    }

    const previousTier = automatedTiers[tierIndex - 1];
    if (previousTier.threshold === undefined || currentTier.threshold <= previousTier.threshold) {
      setTierThresholdErrors((prevErrors) => ({
        ...prevErrors,
        [tierId]: 'Value must be greater than the previous tier.',
      }));
    } else {
      setTierThresholdErrors((prevErrors) => ({
        ...prevErrors,
        [tierId]: undefined, // Clear error if valid
      }));
    }
  };

  const getAvailablePayoutOptionsForTier = (currentTierId: string) => {
    const currentTierBeingEdited = automatedTiers.find((t) => t.id === currentTierId);

    const usedPayoutOptionIdsInOtherTiers = automatedTiers
      .filter((tier) => tier.id !== currentTierId && tier.payoutLabel !== undefined)
      .map((tier) => tier.payoutLabel);

    return payoutOptions.filter((option) => {
      if (currentTierBeingEdited && option.label === currentTierBeingEdited.payoutLabel) {
        return true;
      }
      return !usedPayoutOptionIdsInOtherTiers.includes(option.label);
    });
  };

  const getTierAggregationDurationLabel = () => {
    return `${offerAutomationConfig?.automationType === AutomatedPayoutType.SALE
      ? 'sales'
      : 'conversions'
      } generated by a member is`;
  };

  const canAddMoreTiers = automatedTiers.length < payoutOptions.length;

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleCancel();
        } else {
          onOpenChange(isOpen);
        }
      }}
    >
      <SheetContent className="flex flex-col h-full bg-white p-0 gap-0 w-[576px] sm:max-w-[576px]">
        <SheetHeader className="p-4 border-b flex flex-row items-center justify-start">
          <div className="mt-2 w-6 h-6 bg-blue-400 rounded-lg flex items-center justify-center mr-2">
            <BadgePercentIcon className="w-4 h-4 text-white" />
          </div>
          <SheetTitle className="m-0 text-base font-medium mr-3">Automated Tiers</SheetTitle>
          {offerAutomationConfig.isActive ? (
            <Tag bgColor="bg-green-4" textColor="text-grey-0" label="Active" className="w-auto" />
          ) : (
            <Tag bgColor="bg-yellow-50" textColor="text-yellow-600" label="Disabled" className="w-auto" />
          )}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-5 py-[10px] space-y-4">
          <div className="self-stretch inline-flex flex-col justify-start items-start gap-1">
            <div className="self-stretch justify-start text-gray-800 text-base font-medium font-['Inter'] leading-7">Automate Your Payouts with Tiered Commissions</div>
            <div className="self-stretch justify-start text-gray-500 text-sm font-normal font-['SF_Pro_Text'] leading-snug">Set up payout tiers based on the number of conversions or total sales generated by members</div>
          </div>

          <div className="space-y-6 flex flex-col">
            {/* start */}
            <div>
              <div className="self-stretch pb-2 inline-flex justify-start items-center gap-1">
                <div className="justify-start text-gray-800 text-xs font-medium font-['Inter'] leading-tight">Automated Tiers are based on</div>
              </div>
              <Select value={offerAutomationConfig?.automationType} onValueChange={handleAutomationTypeChange}>
                <SelectTrigger id="automationType" className="bg-grey-0 self-stretch justify-between text-gray-800 text-sm font-normal font-['Inter'] leading-normal">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-grey-0">
                  <SelectItem value={AutomatedPayoutType.SALE}>Total sale amount</SelectItem>
                  <SelectItem value={AutomatedPayoutType.CONVERSION}>Number of conversions</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Updated Tier Aggregation Duration Section */}
            <div className="self-stretch flex justify-between items-start flex-col">
              <div className="w-full inline-flex justify-between items-center gap-1 pb-2">
                <div className="flex items-center gap-1">
                  <div className="justify-start whitespace-nowrap text-gray-800 text-xs font-medium font-['Inter'] leading-tight">When the</div>
                  <Select
                    value={offerAutomationConfig?.tierAggregationDuration}
                    onValueChange={handleTierAggregationDurationChange}
                  >
                    <SelectTrigger
                      id="tierAggregationDuration"
                      className="w-auto h-6 px-2 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 flex justify-between items-center"
                    >
                      <SelectValue placeholder="Select duration" className="text-gray-800 text-sm font-normal font-['Inter'] leading-normal" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value={TierAggregationDurationTypes.MONTHLY} className="text-gray-800 text-sm">Monthly</SelectItem>
                      <SelectItem value={TierAggregationDurationTypes.QUARTERLY} className="text-gray-800 text-sm">Quarterly</SelectItem>
                      <SelectItem value={TierAggregationDurationTypes.TOTAL} className="text-gray-800 text-sm">Total</SelectItem>
                      <SelectItem value={TierAggregationDurationTypes.YEARLY} className="text-gray-800 text-sm">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="justify-start whitespace-nowrap text-gray-800 text-xs font-medium font-['Inter'] leading-tight">
                    {getTierAggregationDurationLabel()}
                  </div>
                </div>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Switch
                        checked={offerAutomationConfig.isActive}
                        onCheckedChange={(checked) => {
                          setOfferAutomationConfig((prevConfig) => ({
                            ...prevConfig,
                            isActive: checked,
                          }));
                        }}
                        aria-label="Toggle automated payouts"
                        className="pointer-events-auto"
                      />
                    </TooltipTrigger>
                    <TooltipContent className="bg-grey-6 text-grey-0 p-2 max-w-[300px] text-sm">
                      {offerAutomationConfig.isActive ? 'Turn Off automation' : 'Turn On automation'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="space-y-4 w-full border bg-gray-50 border-dashed border-grey-3 rounded-xl p-4">
                <div className="flex flex-col gap-1">
                  {automatedTiers.map((tier, index) => (
                    <div key={tier.id} className="flex gap-2 self-stretch items-center">
                      {/* Input Group (Prefix + Input) */}
                      <div className="w-60 h-8 px-1.5 py-[5px] relative bg-gray-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex justify-start items-center gap-2 overflow-hidden flex-1">
                        <div className="w-14 h-8 left-0 top-[1px] absolute bg-gray-200 flex justify-center items-center">
                          <div className="text-center items-center justify-center text-gray-800 text-sm ">
                            {'>='} {offerAutomationConfig?.automationType === AutomatedPayoutType.SALE && ' $'}
                          </div>
                        </div>
                        <div className="w-8 h-0 origin-top-left rotate-90 outline outline-1 outline-offset-[-0.50px] outline-gray-200" />
                        <div className="w-full flex relative justify-center items-center ml-3">
                          <Input
                            type="number"
                            value={tier.threshold === undefined ? '' : tier.threshold}
                            onChange={(e) => handleTierChange(tier.id, 'threshold', e.target.value)}
                            onBlur={() => handleThresholdBlur(tier.id, index)}
                            placeholder="0"
                            className={`flex-1 pl-2 ml-2 h-full border-none text-sm text-gray-800 ${index != 0 ? 'bg-white' : ''} ${tierThresholdErrors[tier.id] ? 'border-red-500 !border' : ''}`}
                            disabled={index === 0}
                          />
                        </div>
                      </div>

                      {/* Select Payout */}
                      <div className="flex-1 self-stretch inline-flex justify-start items-center gap-2">
                        <Select
                          value={tier.payoutLabel}
                          onValueChange={(value) => handleTierChange(tier.id, 'payoutLabel', value)}
                        >
                          <SelectTrigger className={`flex-1 px-3 py-1 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 flex justify-between items-center gap-2 ${index === 0 ? 'w-full' : ''}`}>
                            <SelectValue
                              placeholder="Select payout"
                              className="flex-1 justify-start text-gray-800 text-sm font-normal font-['Inter'] leading-normal"
                            />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-grey-200">
                            {getAvailablePayoutOptionsForTier(tier.id).map((option) => (
                              <SelectItem key={option.id} value={option.label} className="text-grey-800">
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Delete Button or Placeholder */}
                      {index > 0 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteTier(tier.id)}
                          className="text-grey-500 hover:text-red-600 h-10 w-10 flex-shrink-0"
                          aria-label="Delete tier"
                        >
                          <XmarkIcon className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    variant="link"
                    onClick={handleAddTier}
                    className="text-gray-600 text-sm font-medium self-start p-0 h-auto mt-1 underline hover:text-gray-700 disabled:text-grey-400 disabled:cursor-not-allowed"
                    disabled={!canAddMoreTiers || !offerAutomationConfig.isActive}
                  >
                    <PlusIcon className="h-4 w-4 mr-1.5" />
                    Add tier
                  </Button>
                </div>
              </div>
            </div>

            {Object.values(tierThresholdErrors).some((e) => e) && (
              <Alert variant="destructive" className="m-1">
                <AlertDescription>
                  <ul className="list-inside list-disc text-sm">
                    <li>Each tier value must be greater than the previous tier.</li>
                    <li>Please fix the errors in the tier thresholds.</li>
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            <div className="border-grey-200">
              <Collapsible open={isSettingsExpanded} onOpenChange={setIsSettingsExpanded}>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex justify-start w-full px-1 py-2 text-left hover:bg-grey-100 rounded-md"
                  >
                    <Label className="text-sm font-medium text-gray-800 cursor-pointer">Settings</Label>
                    {isSettingsExpanded ? (
                      <ChevronUpIcon className="h-4 ml-2 w-4 text-gray-600" />
                    ) : (
                      <ChevronDownIcon className="h-4 ml-2 w-4 text-gray-600" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-4 space-y-4 animate-collapsible-down">

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="showInCreatorPortalCheckboxAutomateDrawer"
                      checked={!!offerAutomationConfig?.showInCreatorPortal}
                      onCheckedChange={(checked) => {
                        setOfferAutomationConfig((prevConfig) => ({
                          ...prevConfig,
                          showInCreatorPortal: checked as boolean,
                        }));
                      }}
                    />
                    <Label htmlFor="showInCreatorPortalCheckboxAutomateDrawer" className="text-sm font-medium text-grey-6">Show automated payout tiers in creator portal</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <CircleInfoIcon className="w-3 h-3 text-grey-3" />
                        </TooltipTrigger>
                        <TooltipContent className="bg-grey-6 text-grey-0 p-2 max-w-[300px] text-sm">
                          Creator Portal will show the automated payout details to the members.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  {offerAutomationConfig?.tierAggregationDuration === TierAggregationDurationTypes.TOTAL && (
                    <div className="space-y-3 p-4 rounded-lg border border-grey-200 bg-white shadow-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-400 rounded-lg flex items-center justify-center mr-2">
                          <CalendarDaysIcon className="w-4 h-4 text-white" />
                        </div>
                        <Label className="text-sm font-medium text-gray-800">Lookback Window</Label>
                      </div>
                      <p className="text-sm text-gray-700">
                        Choose the date from which we should start checking member conversions to calculate their payout tiers.
                      </p>
                      <AutomatedOfferStartDate
                        value={offerAutomationConfig.automationStartDate || null}
                        handleAutomationStartDateChange={handleAutomationStartDateChange}
                        disabled={false}
                      />
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>

          </div>
        </div>

        <Dialog open={showConfirmActivationDialog} onOpenChange={setShowConfirmActivationDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <ExclamationCircleFilled style={{ color: '#E2AB23', marginRight: '8px' }} />
                Switch to Automated Tiers
              </DialogTitle>
              <DialogDescription>
                <div className="self-stretch pl-7 inline-flex flex-col justify-start items-start gap-4">
                  <div className="self-stretch justify-center">
                    <span className="text-gray-500 text-sm font-normal font-['Inter'] leading-normal">
                      Are you sure you want to switch to automated payout tiers?<br />
                    </span>
                    <ul className="text-gray-500 text-sm font-normal font-['Inter'] leading-normal list-disc pl-4">
                      <li>All members having default payouts will be moved to the new tier structure.</li>
                      <li>All future conversions for them will follow the new tier structure.</li>
                    </ul>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={cancelActivationConfirmation}>
                Cancel
              </Button>
              <Button variant="primary" onClick={executeActivationConfirmed}>
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deactivation Step 1: Pause Prompt Dialog */}
        <Dialog open={showPausePromptDialog} onOpenChange={setShowPausePromptDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <ExclamationCircleFilled style={{ color: '#E2AB23', marginRight: '8px' }} />
                Pause Automated tiers
              </DialogTitle>
              <DialogDescription className="py-4">
                To pause automated tiers, you must select a default payout value to assign to all members that belong to automated tiers.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={cancelPauseProcess}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleProceedToSelectDefault}>
                Select Default Payout
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deactivation Step 2: Select Default Payout Dialog */}
        <Dialog open={showSelectDefaultPayoutDialog} onOpenChange={setShowSelectDefaultPayoutDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Select default payout
              </DialogTitle>
              <DialogDescription className="py-4">
                Select a payout tier from the available options.
                {payoutOptions.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    There are no payout options available to set as default. The offer will have no default payout if automation is paused.
                  </p>
                )}

                {payoutOptions.length > 0 && (
                  <div className="w-auto space-y-2 py-2 justify-center items-center">
                    <Select
                      value={selectedDefaultPayoutLabel}
                      onValueChange={setSelectedDefaultPayoutLabel}
                    >
                      <SelectTrigger id="defaultPayoutSelectDeactivate" className="w-full bg-white">
                        <SelectValue placeholder="Select a default payout" />
                      </SelectTrigger>
                      <SelectContent>
                        {payoutOptions.map((opt) => (
                          <SelectItem key={opt.label} value={opt.label}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={cancelPauseProcess}>
                Cancel
              </Button>
              <Button variant="primary" onClick={executeDeactivationConfirmed} disabled={payoutOptions.length > 0 && !selectedDefaultPayoutLabel}>
                Pause Automations
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <SheetFooter className="justify-start sm:justify-start p-4 border-t">
          <Button onClick={handleDone} variant="primary" disabled={isRequiredFieldsMissing || (!hasUnsavedChanges && !!offerAutomationConfig?.id)}>
            Done
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
